// CitationService.js
// Service for processing and managing citations with enhanced metadata

/**
 * CitationService - Manages citation processing, grouping, and real-time updates
 * Handles both basic citations (immediate) and enhanced citations (from background processing)
 */
class CitationService {
  constructor() {
    this.citationCache = new Map();
    this.metadataCache = new Map();
    this.processingCallbacks = new Map();
    this.apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '';
  }

  /**
   * Process citations for display with enhanced metadata
   * @param {Array} citations - Array of citation objects
   * @param {string} messageId - Message identifier
   * @param {boolean} isEnhanced - Whether these are enhanced citations from background processing
   * @returns {Object} Processed citation data with grouping and summary
   */
  async processCitations(citations, messageId, isEnhanced = false) {
    console.log(`🔍 Processing ${citations.length} citations for message ${messageId} (enhanced: ${isEnhanced})`);
    
    const processedCitations = [];

    for (const citation of citations) {
      const processed = await this.processSingleCitation(citation, messageId, isEnhanced);
      processedCitations.push(processed);
    }

    // Group citations by source type
    const groupedCitations = this.groupCitationsByType(processedCitations);
    
    // Calculate summary statistics
    const summary = this.calculateCitationSummary(processedCitations);

    // Cache the processed data
    const cacheKey = `${messageId}_${isEnhanced ? 'enhanced' : 'basic'}`;
    this.citationCache.set(cacheKey, {
      citations: processedCitations,
      grouped: groupedCitations,
      summary: summary,
      isEnhanced: isEnhanced,
      processedAt: new Date().toISOString()
    });

    console.log(`✅ Processed ${processedCitations.length} citations with ${summary.uniqueDataSources} unique sources`);

    return {
      citations: processedCitations,
      grouped: groupedCitations,
      summary: summary,
      isEnhanced: isEnhanced,
      messageId: messageId
    };
  }

  /**
   * Process a single citation with UI-specific enhancements
   * @param {Object} citation - Citation object
   * @param {string} messageId - Message identifier
   * @param {boolean} isEnhanced - Whether this is an enhanced citation
   * @returns {Object} Processed citation with display properties
   */
  async processSingleCitation(citation, messageId, isEnhanced) {
    // Generate unique ID if not present
    const citationId = citation.id || `${messageId}_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
    
    const processed = {
      ...citation,
      id: citationId,
      messageId: messageId,
      isEnhanced: isEnhanced,
      
      // UI-specific properties
      displayName: this.generateDisplayName(citation),
      icon: this.getSourceIcon(citation.sourceType),
      color: this.getSourceColor(citation.sourceType),
      badge: this.generateBadgeText(citation),
      relevanceLevel: this.categorizeRelevance(citation.confidence),
      
      // Display states
      isExpanded: false,
      isHighlighted: false,
      
      // Enhanced properties (if available)
      hasMetadata: !!(citation.metadata && Object.keys(citation.metadata).length > 0),
      hasContentAnalysis: !!(citation.contentAnalysis),
      hasDisplayProperties: !!(citation.displayProperties),
      
      // Processing info
      processedAt: new Date().toISOString(),
      enhancementLevel: isEnhanced ? 'full' : 'basic'
    };

    // Add enhanced display properties if available
    if (citation.displayProperties) {
      processed.displayProperties = citation.displayProperties;
      processed.displayName = citation.displayProperties.displayName || processed.displayName;
      processed.icon = citation.displayProperties.icon || processed.icon;
      processed.color = citation.displayProperties.color || processed.color;
    }

    // Cache individual citation
    this.citationCache.set(citationId, processed);

    return processed;
  }

  /**
   * Group citations by source type for tabbed display
   * @param {Array} citations - Processed citations
   * @returns {Object} Grouped citations
   */
  groupCitationsByType(citations) {
    const groups = {
      all: citations,
      documents: [],
      knowledgeBases: [],
      web: [],
      other: []
    };

    citations.forEach(citation => {
      switch (citation.sourceType) {
        case 'pdf':
        case 'document':
        case 'text':
          groups.documents.push(citation);
          break;
        case 'kendra':
        case 's3':
          groups.knowledgeBases.push(citation);
          break;
        case 'web':
          groups.web.push(citation);
          break;
        default:
          groups.other.push(citation);
      }
    });

    return groups;
  }

  /**
   * Calculate comprehensive summary statistics
   * @param {Array} citations - Processed citations
   * @returns {Object} Summary statistics
   */
  calculateCitationSummary(citations) {
    const summary = {
      total: citations.length,
      enhanced: citations.filter(c => c.isEnhanced).length,
      basic: citations.filter(c => !c.isEnhanced).length,
      
      // By type
      byType: this.getCountByType(citations),
      
      // Confidence analysis
      averageConfidence: this.calculateAverageConfidence(citations),
      confidenceDistribution: this.calculateConfidenceDistribution(citations),
      
      // Source analysis
      uniqueDataSources: [...new Set(citations.map(c => c.source))].length,
      topSources: this.getTopSources(citations, 5),
      
      // Content analysis (for enhanced citations)
      hasContentAnalysis: citations.some(c => c.contentAnalysis),
      averageWordCount: this.calculateAverageWordCount(citations),
      topTopics: this.getTopTopics(citations, 5),
      
      // Processing info
      enhancementRate: citations.length > 0 ? (citations.filter(c => c.isEnhanced).length / citations.length) * 100 : 0,
      lastProcessed: new Date().toISOString()
    };

    return summary;
  }

  /**
   * Merge basic citations with enhanced citations from background processing
   * @param {string} messageId - Message identifier
   * @param {Array} enhancedCitations - Enhanced citations from background processing
   * @returns {Object} Merged citation data
   */
  async mergeEnhancedCitations(messageId, enhancedCitations) {
    console.log(`🔄 Merging enhanced citations for message ${messageId}`);
    
    // Get existing basic citations
    const basicCacheKey = `${messageId}_basic`;
    const basicData = this.citationCache.get(basicCacheKey);
    
    if (!basicData) {
      console.log(`📭 No basic citations found for ${messageId}, using enhanced only`);
      return await this.processCitations(enhancedCitations, messageId, true);
    }

    // Create a map of enhanced citations by source for easy lookup
    const enhancedMap = new Map();
    enhancedCitations.forEach(citation => {
      const key = `${citation.source}_${citation.content?.substring(0, 100)}`;
      enhancedMap.set(key, citation);
    });

    // Merge basic with enhanced
    const mergedCitations = basicData.citations.map(basicCitation => {
      const key = `${basicCitation.source}_${basicCitation.content?.substring(0, 100)}`;
      const enhanced = enhancedMap.get(key);
      
      if (enhanced) {
        console.log(`🔗 Merging enhanced data for: ${basicCitation.source}`);
        return {
          ...basicCitation,
          ...enhanced,
          id: basicCitation.id, // Keep original ID
          isEnhanced: true,
          enhancementLevel: 'merged',
          mergedAt: new Date().toISOString()
        };
      }
      
      return basicCitation;
    });

    // Add any new enhanced citations that weren't in basic set
    enhancedCitations.forEach(enhanced => {
      const key = `${enhanced.source}_${enhanced.content?.substring(0, 100)}`;
      const exists = basicData.citations.some(basic => 
        `${basic.source}_${basic.content?.substring(0, 100)}` === key
      );
      
      if (!exists) {
        console.log(`➕ Adding new enhanced citation: ${enhanced.source}`);
        mergedCitations.push({
          ...enhanced,
          isEnhanced: true,
          enhancementLevel: 'new'
        });
      }
    });

    // Process the merged citations
    const processedData = await this.processCitations(mergedCitations, messageId, true);
    
    console.log(`✅ Merged ${basicData.citations.length} basic + ${enhancedCitations.length} enhanced = ${mergedCitations.length} total citations`);
    
    return processedData;
  }

  /**
   * Get processed citations from cache
   * @param {string} messageId - Message identifier
   * @param {boolean} preferEnhanced - Whether to prefer enhanced over basic
   * @returns {Object|null} Cached citation data
   */
  getCachedCitations(messageId, preferEnhanced = true) {
    const enhancedKey = `${messageId}_enhanced`;
    const basicKey = `${messageId}_basic`;
    
    if (preferEnhanced && this.citationCache.has(enhancedKey)) {
      return this.citationCache.get(enhancedKey);
    }
    
    if (this.citationCache.has(basicKey)) {
      return this.citationCache.get(basicKey);
    }
    
    return null;
  }

  /**
   * Register callback for when enhanced citations are processed
   * @param {string} messageId - Message identifier
   * @param {Function} callback - Callback function
   */
  onEnhancedCitationsReady(messageId, callback) {
    if (!this.processingCallbacks.has(messageId)) {
      this.processingCallbacks.set(messageId, []);
    }
    this.processingCallbacks.get(messageId).push(callback);
  }

  /**
   * Trigger callbacks when enhanced citations are ready
   * @param {string} messageId - Message identifier
   * @param {Object} enhancedData - Enhanced citation data
   */
  triggerEnhancedCitationsReady(messageId, enhancedData) {
    const callbacks = this.processingCallbacks.get(messageId);
    if (callbacks) {
      callbacks.forEach(callback => {
        try {
          callback(enhancedData);
        } catch (error) {
          console.error('Error in enhanced citations callback:', error);
        }
      });
      this.processingCallbacks.delete(messageId);
    }
  }

  /**
   * Fetch enhanced metadata from API
   * @param {string} messageId - Message identifier
   * @returns {Object|null} Enhanced metadata
   */
  async fetchEnhancedMetadata(messageId) {
    try {
      console.log(`🌐 Fetching enhanced metadata for message ${messageId}`);
      
      const response = await fetch(`${this.apiBaseUrl}/metadata/${messageId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 404) {
          console.log(`📭 No enhanced metadata found for message ${messageId}`);
          return null;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success && result.data) {
        console.log(`✅ Retrieved enhanced metadata for ${messageId}:`, {
          citationsCount: result.data.citations?.length || 0,
          hasMetadata: !!result.data.metadata,
          processingTime: result.data.processingTimeMs
        });
        
        return result.data;
      }
      
      return null;
    } catch (error) {
      console.error(`❌ Error fetching enhanced metadata for ${messageId}:`, error);
      return null;
    }
  }

  /**
   * Helper methods for citation processing
   */

  generateDisplayName(citation) {
    // Use enhanced display name if available
    if (citation.displayProperties?.displayName) {
      return citation.displayProperties.displayName;
    }
    
    if (citation.source && citation.source !== 'Unknown Source' && citation.source !== 'Unknown') {
      // Clean up filename for display
      return citation.source
        .replace(/\.[^/.]+$/, '') // Remove extension
        .replace(/[-_]/g, ' ') // Replace dashes and underscores with spaces
        .replace(/\b\w/g, l => l.toUpperCase()); // Title case
    }
    
    return 'Unknown Document';
  }

  getSourceIcon(sourceType) {
    const iconMap = {
      'pdf': 'file-pdf',
      'document': 'file',
      'text': 'file-text',
      'kendra': 'search',
      's3': 'folder',
      'web': 'external',
      'json': 'file-code'
    };
    
    return iconMap[sourceType] || 'file';
  }

  getSourceColor(sourceType) {
    const colorMap = {
      'pdf': 'red',
      'document': 'blue',
      'text': 'green',
      'kendra': 'orange',
      's3': 'purple',
      'web': 'teal',
      'json': 'grey'
    };
    
    return colorMap[sourceType] || 'grey';
  }

  generateBadgeText(citation) {
    const confidence = citation.confidence || 0;
    
    if (confidence >= 0.8) return 'High Confidence';
    if (confidence >= 0.5) return 'Medium Confidence';
    return 'Low Confidence';
  }

  categorizeRelevance(confidence) {
    if (!confidence) return 'unknown';
    if (confidence >= 0.8) return 'high';
    if (confidence >= 0.5) return 'medium';
    return 'low';
  }

  getCountByType(citations) {
    const counts = {};
    citations.forEach(citation => {
      const type = citation.sourceType || 'unknown';
      counts[type] = (counts[type] || 0) + 1;
    });
    return counts;
  }

  calculateAverageConfidence(citations) {
    if (citations.length === 0) return 0;
    
    const total = citations.reduce((sum, citation) => sum + (citation.confidence || 0), 0);
    return Math.round((total / citations.length) * 100) / 100;
  }

  calculateConfidenceDistribution(citations) {
    const distribution = { high: 0, medium: 0, low: 0, unknown: 0 };
    
    citations.forEach(citation => {
      const level = this.categorizeRelevance(citation.confidence);
      distribution[level]++;
    });

    return distribution;
  }

  getTopSources(citations, limit = 5) {
    const sourceCounts = {};
    citations.forEach(citation => {
      const source = citation.source || 'Unknown';
      sourceCounts[source] = (sourceCounts[source] || 0) + 1;
    });

    return Object.entries(sourceCounts)
      .sort(([,a], [,b]) => b - a)
      .slice(0, limit)
      .map(([source, count]) => ({ source, count }));
  }

  calculateAverageWordCount(citations) {
    const citationsWithAnalysis = citations.filter(c => c.contentAnalysis?.wordCount);
    if (citationsWithAnalysis.length === 0) return 0;
    
    const total = citationsWithAnalysis.reduce((sum, c) => sum + c.contentAnalysis.wordCount, 0);
    return Math.round(total / citationsWithAnalysis.length);
  }

  getTopTopics(citations, limit = 5) {
    const topicCounts = {};
    
    citations.forEach(citation => {
      if (citation.contentAnalysis?.topics) {
        citation.contentAnalysis.topics.forEach(topic => {
          topicCounts[topic] = (topicCounts[topic] || 0) + 1;
        });
      }
    });

    return Object.entries(topicCounts)
      .sort(([,a], [,b]) => b - a)
      .slice(0, limit)
      .map(([topic, count]) => ({ topic, count }));
  }

  /**
   * Clear cache for memory management
   * @param {string} messageId - Optional message ID to clear specific cache
   */
  clearCache(messageId = null) {
    if (messageId) {
      const keysToDelete = [];
      for (const key of this.citationCache.keys()) {
        if (key.includes(messageId)) {
          keysToDelete.push(key);
        }
      }
      keysToDelete.forEach(key => this.citationCache.delete(key));
      this.processingCallbacks.delete(messageId);
      console.log(`🧹 Cleared cache for message ${messageId}`);
    } else {
      this.citationCache.clear();
      this.metadataCache.clear();
      this.processingCallbacks.clear();
      console.log('🧹 Cleared all citation cache');
    }
  }

  /**
   * Get cache statistics for debugging
   * @returns {Object} Cache statistics
   */
  getCacheStats() {
    return {
      citationCacheSize: this.citationCache.size,
      metadataCacheSize: this.metadataCache.size,
      pendingCallbacks: this.processingCallbacks.size,
      cacheKeys: Array.from(this.citationCache.keys())
    };
  }
}

// Create singleton instance
const citationService = new CitationService();

export default citationService;